import {
    ApplicationRef,
    ComponentRef,
    Injectable,
    Type,
    createComponent,
    inject,
    reflectComponentType
} from "@angular/core";
import { GridSettings } from "@handsontable/angular-wrapper";
import Handsontable from "handsontable";

@Injectable({
    providedIn: "root"
})
export class HotCellAngularRendererService {
    private readonly appRef = inject(ApplicationRef);

    // Lista med renderade komponenter. Används för att kunna destroya alla vid olika tillfällen när de inte behövs.
    // Dictionary/Map av komponenter, nycklad på handsontable.guid -> Array av renderade komponenter
    private readonly allRenderedComponentReferencesByHotInstance = new Map<string, ComponentRef<IHotCellAngularRenderedCellComponent>[]>();

    renderAngularComponent<T extends IHotCellAngularRenderedCellComponent>(
        hot: Handsontable,
        td: HTMLTableCellElement,
        row: any,
        col: any,
        prop: any,
        value: any,
        cellProperties: GridSettings,
        angularComponentToRender: Type<T>,
        init: (instance: T) => void
    ): HTMLTableCellElement {
        const renderedComponentRef = this.loadComponentInTarget(angularComponentToRender, td, init);
        this.addRenderedComponentRef(hot, renderedComponentRef);
        return td;
    }

    destroyAllComponentsForHotInstance(hot: Handsontable) {
        const handsonTableUniqueId = (hot as any).guid;
        if (this.allRenderedComponentReferencesByHotInstance.has(handsonTableUniqueId)) {
            const renderedComponents = this.allRenderedComponentReferencesByHotInstance.get(handsonTableUniqueId);
            renderedComponents.forEach(compRef => {
                this.destroyComponent(compRef);
            });
            renderedComponents.splice(0);
        }
    }

    private addRenderedComponentRef<T>(hot: Handsontable, componentRef: ComponentRef<T>) {
        const handsonTableUniqueId = (hot as any).guid;
        if (!this.allRenderedComponentReferencesByHotInstance.has(handsonTableUniqueId)) {
            this.allRenderedComponentReferencesByHotInstance.set(handsonTableUniqueId, []);
        }
        this.allRenderedComponentReferencesByHotInstance.get(handsonTableUniqueId).push(componentRef);
    }

    private destroyComponent(compRef: ComponentRef<IHotCellAngularRenderedCellComponent>) {
        this.appRef.detachView(compRef.hostView);
        compRef.destroy();
    }

    private loadComponentInTarget<T>(component: Type<T>, target: Element, onInit?: (component: T) => void): ComponentRef<T> {
        let componentRef: ComponentRef<T>;
        try {

            // Hämtar metadata (mirror) för komponenttypen
            const componentMetadata = reflectComponentType(component);

            // Skapar en container med samma tag/selector som componenten har
            const container = document.createElement(componentMetadata.selector);

            // Overridar handsontables defaultstyling
            container.classList.add("rask-no-handsontable-styling");

            // Tömmer cellen på all data
            this.clearContentsOfElement(target);

            // Lägger in containern i cellen
            target.appendChild(container);

            // Instansierar komponenten och stoppar in den i container
            componentRef = createComponent(component, { environmentInjector: this.appRef.injector,
                hostElement: container });

            if (onInit) {
                // Kör custom init-metod för komponenten
                onInit(componentRef.instance);
            }
            // Triggar igång angular för komponenten
            this.appRef.attachView(componentRef.hostView);
        } catch (e) {
            console.error("Unable to load component", component, "in", target);
            throw e;
        }
        return componentRef;
    }

    private clearContentsOfElement(target: Element) {
        target.innerHTML = "";
    }
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IHotCellAngularRenderedCellComponent {

}
